
import { motion } from 'framer-motion';

const Hero = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-gray-50 via-white to-teal-50 pt-16 md:pt-20">
      {/* Decorative Elements */}
      <div className="absolute top-20 right-10 w-72 h-72 bg-teal-400/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-orange-400/10 rounded-full blur-3xl"></div>

      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-center">
          
          {/* Mobile: Image First - Only visible on mobile */}
          <div className="lg:hidden w-full">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="relative w-full max-w-md mx-auto"
            >
              {/* Main Image - Mobile Optimized */}
              <div className="relative w-full h-[400px] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://public.readdy.ai/ai/img_res/159763e844db81c85f0b87a5ea580902.jpg"
                  alt="Expert vitrier FenetrePro Toulouse - Spécialiste réparation fenêtre"
                  title="Vitrier professionnel certifié - Réparation fenêtre Toulouse"
                  width="600"
                  height="800"
                  loading="eager"
                  fetchpriority="high"
                  className="w-full h-full object-cover object-top"
                />
                
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-teal-900/40 via-transparent to-transparent"></div>

                {/* Experience Badge - Bottom Center on Mobile */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white rounded-xl shadow-xl p-3 flex items-center gap-3 max-w-[90%]">
                  <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-award-line text-white text-xl"></i>
                  </div>
                  <div>
                    <p className="text-xl font-bold text-gray-900">15+ ans</p>
                    <p className="text-xs text-gray-600">d'expérience</p>
                  </div>
                  <div className="w-px h-10 bg-gray-200 mx-1"></div>
                  <div>
                    <p className="text-xl font-bold text-gray-900">2500+</p>
                    <p className="text-xs text-gray-600">clients</p>
                  </div>
                </div>
              </div>

              {/* Rating Badge - Top Right */}
              <div className="absolute -top-3 right-2 bg-white rounded-xl shadow-xl p-2.5 flex items-center gap-2">
                <div className="flex items-center text-yellow-500">
                  <i className="ri-star-fill text-sm"></i>
                  <i className="ri-star-fill text-sm"></i>
                  <i className="ri-star-fill text-sm"></i>
                  <i className="ri-star-fill text-sm"></i>
                  <i className="ri-star-fill text-sm"></i>
                </div>
                <span className="text-sm text-gray-900 font-bold">4.9/5</span>
              </div>
            </motion.div>
          </div>

          {/* Left Content - Text */}
          <div className="lg:col-span-7 z-10 w-full">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="w-full"
            >
              <div className="inline-flex items-center bg-orange-100 text-orange-600 px-3 py-1.5 md:px-4 md:py-2 rounded-full text-xs md:text-sm font-semibold mb-4 md:mb-6">
                <i className="ri-flashlight-line mr-1.5 md:mr-2"></i>
                Intervention Rapide 24h/24
              </div>

              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-gray-900 mb-4 md:mb-6 leading-tight break-words">
                Expert en <br />
                <span className="text-teal-600">Réparation</span> de <br />
                Fenêtres à <strong>Toulouse</strong>
              </h1>

              <p className="text-base md:text-lg lg:text-xl text-gray-600 mb-6 md:mb-8 leading-relaxed max-w-2xl">
                Spécialiste de la <a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }} className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer underline decoration-teal-600/30 hover:decoration-teal-600 transition-all">réparation de fenêtre</a> PVC, bois, aluminium, <a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }} className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer underline decoration-teal-600/30 hover:decoration-teal-600 transition-all">baie vitrée</a> et <a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }} className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer underline decoration-teal-600/30 hover:decoration-teal-600 transition-all">volet roulant</a>. 
                <strong className="text-gray-900"> Devis gratuit</strong> et <strong className="text-gray-900">garantie 2 ans</strong> sur tous nos travaux.
              </p>

              {/* Trust Points */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 md:gap-3 lg:gap-4 mb-6 md:mb-8 lg:mb-10 w-full">
                <div className="flex items-center">
                  <div className="w-9 h-9 md:w-10 md:h-10 bg-teal-100 rounded-lg flex items-center justify-center mr-2.5 md:mr-3 flex-shrink-0">
                    <i className="ri-check-line text-teal-600 text-lg md:text-xl"></i>
                  </div>
                  <span className="text-sm md:text-base text-gray-700 font-medium break-words">
                    <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToContact(); }} className="hover:text-teal-600 cursor-pointer underline decoration-teal-600/30 hover:decoration-teal-600 transition-all">Devis gratuit</a> sous 24h
                  </span>
                </div>
                <div className="flex items-center">
                  <div className="w-9 h-9 md:w-10 md:h-10 bg-orange-100 rounded-lg flex items-center justify-center mr-2.5 md:mr-3 flex-shrink-0">
                    <i className="ri-check-line text-orange-600 text-lg md:text-xl"></i>
                  </div>
                  <span className="text-sm md:text-base text-gray-700 font-medium break-words">Garantie 2 ans incluse</span>
                </div>
                <div className="flex items-center">
                  <div className="w-9 h-9 md:w-10 md:h-10 bg-teal-100 rounded-lg flex items-center justify-center mr-2.5 md:mr-3 flex-shrink-0">
                    <i className="ri-check-line text-teal-600 text-lg md:text-xl"></i>
                  </div>
                  <span className="text-sm md:text-base text-gray-700 font-medium break-words">
                    <a href="#about" onClick={(e) => { e.preventDefault(); scrollToSection('about'); }} className="hover:text-teal-600 cursor-pointer underline decoration-teal-600/30 hover:decoration-teal-600 transition-all">Artisans certifiés RGE</a>
                  </span>
                </div>
                <div className="flex items-center">
                  <div className="w-9 h-9 md:w-10 md:h-10 bg-orange-100 rounded-lg flex items-center justify-center mr-2.5 md:mr-3 flex-shrink-0">
                    <i className="ri-check-line text-orange-600 text-lg md:text-xl"></i>
                  </div>
                  <span className="text-sm md:text-base text-gray-700 font-medium break-words">Intervention d'urgence</span>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 w-full">
                <button
                  onClick={scrollToContact}
                  className="bg-gradient-to-r from-teal-500 to-teal-600 text-white px-6 md:px-8 py-3 md:py-4 rounded-full font-semibold text-base md:text-lg hover:from-teal-600 hover:to-teal-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer inline-flex items-center justify-center touch-manipulation active:scale-95"
                >
                  <i className="ri-file-text-line mr-2"></i>
                  Devis Gratuit
                </button>
                <a
                  href="tel:0554542545"
                  className="bg-white text-gray-900 px-6 md:px-8 py-3 md:py-4 rounded-full font-semibold text-base md:text-lg hover:bg-gray-50 transition-all shadow-lg hover:shadow-xl border-2 border-gray-200 whitespace-nowrap cursor-pointer inline-flex items-center justify-center touch-manipulation active:scale-95"
                >
                  <i className="ri-phone-line mr-2 text-teal-600"></i>
                  05 54 54 25 45
                </a>
              </div>
            </motion.div>
          </div>

          {/* Right Content - Desktop Only */}
          <div className="hidden lg:block lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              {/* Main Image with Modern Cut Effect */}
              <div className="relative w-full h-[600px] rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://readdy.ai/api/search-image?query=skilled%20professional%20window%20installation%20specialist%20technician%20wearing%20modern%20safety%20equipment%20and%20professional%20work%20attire%20confidently%20holding%20advanced%20repair%20tools%20with%20warm%20friendly%20smile%20demonstrating%20expertise%20and%20trustworthiness%20in%20bright%20contemporary%20setting%20with%20clean%20minimalist%20background%20emphasizing%20professionalism%20quality%20craftsmanship%20and%20customer%20service%20excellence&width=600&height=900&seq=hero-expert-new-v8&orientation=portrait"
                  alt="Expert vitrier FenetrePro Toulouse - Artisan certifié RGE"
                  title="Vitrier professionnel Toulouse - Réparation fenêtre PVC bois alu"
                  width="600"
                  height="900"
                  loading="eager"
                  fetchpriority="high"
                  className="w-full h-full object-cover object-top"
                />
                
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-teal-900/40 via-transparent to-transparent"></div>

                {/* Experience Badge - Bottom Left */}
                <div className="absolute bottom-6 left-6 bg-white rounded-2xl shadow-xl p-4">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center mr-3 flex-shrink-0">
                      <i className="ri-award-line text-white text-2xl"></i>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900">15+</p>
                      <p className="text-sm text-gray-600">Ans d'expérience</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Stats Card - Top Right */}
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="absolute -top-6 -right-6 bg-white rounded-2xl shadow-2xl p-6"
              >
                <div className="flex items-center mb-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                    <i className="ri-user-star-line text-orange-600 text-xl"></i>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">2500+</p>
                    <p className="text-sm text-gray-600">Clients satisfaits</p>
                  </div>
                </div>
                <div className="flex items-center text-yellow-500">
                  <i className="ri-star-fill text-base"></i>
                  <i className="ri-star-fill text-base"></i>
                  <i className="ri-star-fill text-base"></i>
                  <i className="ri-star-fill text-base"></i>
                  <i className="ri-star-fill text-base"></i>
                  <span className="ml-2 text-sm text-gray-600 font-semibold">4.9/5</span>
                </div>
              </motion.div>

              {/* Floating Response Time Card - Bottom Right */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="absolute -bottom-6 -right-6 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl shadow-2xl p-6 text-white"
              >
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mr-3 flex-shrink-0">
                    <i className="ri-time-line text-2xl"></i>
                  </div>
                  <div>
                    <p className="text-3xl font-bold">&lt;24h</p>
                    <p className="text-sm opacity-90">Temps de réponse</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 0.6 }}
        className="absolute bottom-6 md:bottom-8 left-1/2 transform -translate-x-1/2 hidden md:block"
      >
        <div className="flex flex-col items-center cursor-pointer">
          <span className="text-sm text-gray-500 mb-2">Découvrir</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-6 h-10 border-2 border-gray-400 rounded-full flex items-start justify-center p-2"
          >
            <div className="w-1 h-2 bg-gray-400 rounded-full"></div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
